/**
 * Content Store
 * State management for site content and section descriptions
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { supabase, isAbortError } from '@/lib/supabase'
import type { SiteContent, CreateSiteContent, UpdateSiteContent } from '@/types/site_content.types'
import { useUiStore } from './ui.store'
import { config } from '@/config'

export const useContentStore = defineStore('content', () => {
    // State with Local Storage Cache Hydration
    const CACHE_KEY = 'mondai-cache-content'
    const cached = localStorage.getItem(CACHE_KEY)
    const content = ref<SiteContent[]>(cached ? JSON.parse(cached) : [])
    
    // Only show loading state if we have absolutely no cached data
    const isLoading = ref(content.value.length === 0)
    const error = ref<string | null>(null)
    const _lastFetchedAt = ref(0)
    
    // Note: If we had cached data from a previous session, we silently update in the background

    function needsRefresh(): boolean {
        const uiStore = useUiStore()
        const staleMs = config.session.dataStaleMinutes * 60 * 1000
        return content.value.length === 0 || 
               Date.now() - _lastFetchedAt.value > staleMs ||
               _lastFetchedAt.value < uiStore.dataInvalidatedAt
    }

    // Getters
    const getContentByKey = computed(() => (page: string, key: string) => {
        return content.value.find(c => c.page === page && c.section_key === key)
    })

    const getContentByPage = computed(() => (page: string) => {
        return content.value.filter(c => c.page === page)
    })

    // Actions
    let pendingController: AbortController | null = null

    async function loadContent(): Promise<void> {
        if (pendingController) pendingController.abort()
        pendingController = new AbortController()

        // Only set the store to "loading" if it's completely empty. 
        // If we have cached data, we do a silent background refresh.
        if (content.value.length === 0) isLoading.value = true
        error.value = null

        try {
            const { data, error: dbError } = await (supabase
                .from('site_content')
                .select('*')
                .order('page')
                .order('section_key') as any)
                .abortSignal(pendingController.signal)

            if (dbError) throw dbError
            
            content.value = data || []
            localStorage.setItem(CACHE_KEY, JSON.stringify(data || []))
            _lastFetchedAt.value = Date.now()
        } catch (e: any) {
            if (isAbortError(e)) return
            console.error('[ContentStore] Failed to load content:', e)
            error.value = e.message || 'Failed to load content'
        } finally {
            isLoading.value = false
        }
    }

    async function createContent(newContent: CreateSiteContent): Promise<boolean> {
        isLoading.value = true
        error.value = null
        try {
            const { data, error: dbError } = await supabase
                .from('site_content')
                .insert(newContent)
                .select()

            if (dbError) throw dbError
            
            if (data && data.length > 0) {
                content.value.push(data[0])
            } else {
                throw new Error('Failed to create content: No data returned from server.')
            }
            return true
        } catch (e: any) {
            console.error('Failed to create content:', e)
            error.value = e.message || 'Failed to create content'
            return false
        } finally {
            isLoading.value = false
        }
    }

    async function updateContent(id: string, updates: UpdateSiteContent): Promise<boolean> {
        isLoading.value = true
        error.value = null
        try {
            // Strip id if it accidentally leaked in
            const { id: _, ...payload } = updates as any

            const { data, error: dbError } = await supabase
                .from('site_content')
                .update({ ...payload, updated_at: new Date().toISOString() })
                .eq('id', id)
                .select()

            if (dbError) throw dbError
            
            if (!data || data.length === 0) {
                throw new Error('No content was updated. You may not have permission or the item was deleted.')
            }

            const updatedItem = data[0]
            const index = content.value.findIndex(c => c.id === id)
            if (index !== -1) {
                content.value[index] = updatedItem
            }
            
            return true
        } catch (e: any) {
            console.error('Failed to update content:', e)
            error.value = e.message || 'Failed to update content'
            return false
        } finally {
            isLoading.value = false
        }
    }

    async function deleteContent(id: string): Promise<boolean> {
        isLoading.value = true
        error.value = null
        try {
            const { error: dbError } = await supabase
                .from('site_content')
                .delete()
                .eq('id', id)

            if (dbError) throw dbError
            content.value = content.value.filter(c => c.id !== id)
            return true
        } catch (e: any) {
            console.error('Failed to delete content:', e)
            error.value = e.message || 'Failed to delete content'
            return false
        } finally {
            isLoading.value = false
        }
    }

    return {
        content,
        isLoading,
        error,
        getContentByKey,
        getContentByPage,
        needsRefresh,
        loadContent,
        createContent,
        updateContent,
        deleteContent
    }
})
